/**
 * Provysn Risk Assessment Review Page
 *
 * GRC Admin view to review all risk assessments across all applications.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { authFetchInit, hasAuthSession } from '../utils/authSession';
import { listingTableSx } from '../components/listingTableSx';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Skeleton,
  Tabs,
  Tab,
  IconButton,
  Tooltip,
  Divider,
  Alert,
  Badge,
  Stack,
} from '@mui/material';
import {
  Edit as EditIcon,
  RateReview as RateReviewIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { API_ROOT } from '../config/apiConfig';
import { UI_TOKENS } from '../constants/uiTokens';
import { questionCategories } from '../utils/riskQuestionnaire';
import { ListingPagination, ListingSortSelect } from '../components/ListingPagination';
import { DEFAULT_LISTING_SORT, ListingSortValue, sortListing } from '../utils/listingSort';
import { chipSxFromSurface, getRiskSurface, getStatusSurface } from '../utils/appearanceTokens';
import { useTheme } from '../contexts/ThemeContext';
import type { PaletteMode } from '@mui/material';

const TABLE_COLUMNS = [
  { label: 'Application', width: '16%' },
  { label: 'Owners', width: '12%' },
  { label: 'Submission', width: '14%' },
  { label: 'Assignee', width: '12%' },
  { label: 'Score', width: '7%' },
  { label: 'Risk', width: '9%' },
  { label: 'Status', width: '12%' },
  { label: 'Actions', width: '18%' },
] as const;

const INPUT_SX = {
  '& .MuiInputBase-input': { fontSize: UI_TOKENS.text.bodySm },
  '& .MuiInputLabel-root': { fontSize: UI_TOKENS.text.bodySm },
  '& .MuiSelect-select': { fontSize: UI_TOKENS.text.bodySm },
};

const MENU_SX = {
  PaperProps: {
    sx: {
      '& .MuiMenuItem-root': {
        fontSize: UI_TOKENS.text.bodySm,
        minHeight: UI_TOKENS.menu.itemMinHeight,
        py: UI_TOKENS.menu.itemPaddingY,
      },
    },
  },
};

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

interface OwnerSummary {
  first_name: string;
  last_name: string;
}

interface AssessmentSummary {
  id: string;
  status: string;
  user_id: string;
  submitted_by_name: string | null;
  delegated_by_name: string | null;
  submitted_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  review_notes: string | null;
  answers: Record<string, unknown>;
  risk_level: string | null;
  calculated_risk_score: number | null;
  recommended_controls?: unknown;
}

interface ApplicationRow {
  id: string;
  name: string;
  business_function: string | null;
  risk_level: string | null;
  status: string | null;
  primary_owners: OwnerSummary[];
  secondary_owners: OwnerSummary[];
  assessment: AssessmentSummary | null;
}

interface AllAssessmentsResponse {
  success: boolean;
  applications: ApplicationRow[];
}

interface QuestionOption {
  value: string;
  label: string;
}

interface RiskQuestion {
  id: string;
  question_number: number;
  question_text: string;
  category: string;
  categories?: string[];
  response_type: 'text' | 'numeric' | 'yes_no' | 'dropdown' | 'multi_select';
  options?: QuestionOption[];
  response_options?: unknown;
}

interface RiskTemplate {
  id: string;
  questions: RiskQuestion[];
}

interface GrcAdminUser {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
}

// Derived display status (computed from application + assessment data)
type DisplayStatus =
  | 'Not started'
  | 'In Progress'
  | 'Submitted'
  | 'Under Review'
  | 'Clarification Needed'
  | 'Approved'
  | 'Rejected';

const STATUS_ORDER: DisplayStatus[] = [
  'Not started',
  'In Progress',
  'Submitted',
  'Under Review',
  'Clarification Needed',
  'Approved',
  'Rejected',
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computeDisplayStatus(app: ApplicationRow): DisplayStatus {
  const assessment = app.assessment;
  if (!assessment) return 'Not started';

  switch (assessment.status) {
    case 'Draft':
      return 'In Progress';
    case 'In Review':
      return assessment.assigned_to ? 'Under Review' : 'Submitted';
    case 'Needs More Information':
      return 'Clarification Needed';
    case 'Approved':
      return 'Approved';
    case 'Rejected':
      return 'Rejected';
    default:
      return 'In Progress';
  }
}

function scoreBand(score: number | null | undefined): { level: string; range: string } | null {
  if (score == null || Number.isNaN(Number(score))) return null;
  const value = Number(score);
  if (value <= 25) return { level: 'Low', range: '0–25' };
  if (value <= 50) return { level: 'Medium', range: '26–50' };
  if (value <= 75) return { level: 'High', range: '51–75' };
  return { level: 'Critical', range: '76+' };
}

function controlForScore(score: number): { name: string; sentence: string } {
  if (score <= 25) {
    return {
      name: 'Optional single sign-on',
      sentence: 'This score is Low. SSO and MFA stay optional, and no extra approval is required before the app goes live.',
    };
  }
  if (score <= 50) {
    return {
      name: 'Required single sign-on',
      sentence: 'This score is Medium. SSO is required, privileged users must use MFA, and access governance is recommended.',
    };
  }
  if (score <= 75) {
    return {
      name: 'Required single sign-on',
      sentence: 'This score is High. Every user needs SSO and MFA, and privileged access must be managed in PAM before approval.',
    };
  }
  return {
    name: 'Quarterly review',
    sentence: 'This score is Critical. SSO, MFA, access governance, and PAM are required, plus a quarterly review. The High control set is not enough.',
  };
}

function recommendedControl(assessment: AssessmentSummary): { name: string; sentence: string } | null {
  const fromScore = assessment.calculated_risk_score == null ? null : controlForScore(Number(assessment.calculated_risk_score));
  let controls: unknown = assessment.recommended_controls;
  if (typeof controls === 'string') {
    try {
      controls = JSON.parse(controls);
    } catch {
      controls = null;
    }
  }
  if (Array.isArray(controls) && controls.length > 0) {
    const first = controls.find((control) => control && typeof control === 'object' && 'owner_sentence' in control && (control as { owner_sentence?: string }).owner_sentence)
      || controls[0];
    if (first && typeof first === 'object') {
      const row = first as { control_name?: string; owner_sentence?: string; control_description?: string };
      if (row.owner_sentence) {
        return {
          name: row.control_name || fromScore?.name || '',
          sentence: row.owner_sentence,
        };
      }
    }
  }
  return fromScore;
}

function statusChipProps(status: DisplayStatus): {
  label: string;
  color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
  variant: 'filled' | 'outlined';
  sx?: object;
} {
  switch (status) {
    case 'Not started':
      return { label: 'Not started', color: 'default', variant: 'outlined' };
    case 'In Progress':
      return { label: 'In Progress', color: 'primary', variant: 'filled' };
    case 'Submitted':
      return { label: 'Submitted', color: 'warning', variant: 'filled' };
    case 'Under Review':
      return {
        label: 'Under Review',
        color: 'secondary',
        variant: 'filled',
        sx: { bgcolor: '#7c3aed', color: '#fff' },
      };
    case 'Clarification Needed':
      return { label: 'Clarification Needed', color: 'error', variant: 'outlined' };
    case 'Approved':
      return { label: 'Approved', color: 'success', variant: 'filled' };
    case 'Rejected':
      return { label: 'Rejected', color: 'error', variant: 'filled' };
  }
}

function riskChipProps(riskLevel: string | null | undefined, mode: PaletteMode): {
  label: string;
  sx: object;
} {
  const key = (riskLevel || '').toLowerCase();
  const labels: Record<string, string> = {
    low: 'Low',
    medium: 'Medium',
    high: 'High',
    critical: 'Critical',
  };
  const label = labels[key] || 'Not Defined';
  const surface = getRiskSurface(riskLevel || '', mode);
  return {
    label,
    sx: {
      ...chipSxFromSurface(surface),
      fontWeight: 600,
      fontSize: '0.7rem',
      height: 20,
    },
  };
}

function formatOwners(owners: OwnerSummary[]): string {
  if (!owners || owners.length === 0) return '—';
  const names = owners.map((o) => `${o.first_name} ${o.last_name}`);
  if (names.length <= 2) return names.join(', ');
  return `${names[0]}, ${names[1]} +${names.length - 2} more`;
}

function formatDateTime(isoString: string | null | undefined): string {
  if (!isoString) return '—';
  try {
    const d = new Date(isoString);
    return d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '—';
  }
}

function formatAnswer(value: unknown, question?: RiskQuestion): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    // Try to map option values to labels
    if (question?.options) {
      return value
        .map((v) => {
          const opt = question.options!.find((o) => o.value === v);
          return opt ? opt.label : String(v);
        })
        .join(', ');
    }
    const ro = question?.response_options;
    if (Array.isArray(ro)) {
      return value
        .map((v) => {
          const opt = ro.find((o: any) => String(o?.value) === String(v));
          return opt && typeof opt === 'object' && opt.label != null ? String(opt.label) : String(v);
        })
        .join(', ');
    }
    return value.join(', ');
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (String(value).toLowerCase() === 'na') return 'N/A';
  if (question?.response_type === 'yes_no') {
    if (value === 'yes' || value === true) return 'Yes';
    if (value === 'no' || value === false) return 'No';
  }
  if (question?.options) {
    const opt = question.options.find((o) => o.value === String(value));
    return opt ? opt.label : String(value);
  }
  const ro = question?.response_options;
  if (Array.isArray(ro)) {
    const opt = ro.find((o: any) => String(o?.value) === String(value));
    if (opt && typeof opt === 'object' && opt.label != null) return String(opt.label);
  }
  return String(value);
}

function normalizeQuestionRow(q: Record<string, unknown>): RiskQuestion {
  const opts =
    (q.options as QuestionOption[] | undefined) ||
    (Array.isArray(q.response_options)
      ? (q.response_options as Record<string, unknown>[]).map((o) =>
          typeof o === 'object' && o !== null
            ? {
                value: String((o as { value?: unknown }).value ?? (o as { label?: unknown }).label ?? ''),
                label: String((o as { label?: unknown }).label ?? (o as { value?: unknown }).value ?? ''),
              }
            : { value: String(o), label: String(o) }
        )
      : undefined);
  const rt = (q.response_type as RiskQuestion['response_type']) || 'text';
  return { ...(q as object), options: opts, response_type: rt } as RiskQuestion;
}

function normalizeQuestionsFromApi(rows: unknown[]): RiskQuestion[] {
  if (!Array.isArray(rows)) return [];
  return rows.map((row) => normalizeQuestionRow(row as Record<string, unknown>));
}

/** Caption under review title: matches template.active_frameworks (owner scope). */
function formatFrameworkScopeHint(activeFrameworks: unknown): string {
  if (!Array.isArray(activeFrameworks) || activeFrameworks.length === 0) {
    return 'Framework scope: all (no restriction — same questions application owners see).';
  }
  const labels = activeFrameworks.map((x) => String(x).trim()).filter(Boolean);
  if (!labels.length) {
    return 'Framework scope: all (no restriction — same questions application owners see).';
  }
  return `Framework scope: ${labels.join(', ')} — same as application owners.`;
}

function isAnswerEmpty(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
}

/** Read-only display that mirrors owner questionnaire control types (disabled). */
const ReadOnlyRiskAnswer: React.FC<{ question: RiskQuestion; value: unknown }> = ({ question, value }) => {
  const empty = isAnswerEmpty(value);
  const rt = question.response_type || 'text';
  const opts = question.options || [];

  if (empty) {
    return (
      <Typography variant="body2" color="text.secondary" fontStyle="italic">
        Not answered
      </Typography>
    );
  }

  if (rt === 'yes_no') {
    const v = String(value).toLowerCase();
    const sel = v === 'true' ? 'yes' : v === 'false' ? 'no' : v;
    if (sel === 'na' || sel === 'n/a') {
      return <Chip label="N/A" size="small" variant="outlined" sx={{ fontWeight: 600 }} />;
    }
    const yes = sel === 'yes';
    const no = sel === 'no';
    if (!yes && !no) {
      return <Typography variant="body2">{formatAnswer(value, question)}</Typography>;
    }
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip label="Yes" size="small" color={yes ? 'success' : 'default'} variant={yes ? 'filled' : 'outlined'} />
        <Chip label="No" size="small" color={no ? 'error' : 'default'} variant={no ? 'filled' : 'outlined'} />
      </Stack>
    );
  }

  if (rt === 'dropdown') {
    const strVal = String(value);
    if (!opts.length) {
      return (
        <TextField fullWidth size="small" value={formatAnswer(value, question)} disabled multiline />
      );
    }
    const hasOption = opts.some((o) => o.value === strVal);
    return (
      <FormControl fullWidth size="small" variant="outlined">
        <Select value={strVal} disabled displayEmpty>
          {opts.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
          {!hasOption ? (
            <MenuItem value={strVal}>{formatAnswer(value, question)}</MenuItem>
          ) : null}
        </Select>
      </FormControl>
    );
  }

  if (rt === 'multi_select') {
    const arr = Array.isArray(value) ? value.map((x) => String(x)) : [String(value)];
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.75}>
        {arr.map((v) => {
          const opt = opts.find((o) => o.value === v);
          return <Chip key={v} size="small" label={opt?.label ?? v} variant="outlined" />;
        })}
      </Stack>
    );
  }

  if (rt === 'numeric') {
    return <TextField fullWidth size="small" type="number" value={String(value)} disabled />;
  }

  return (
    <TextField
      fullWidth
      size="small"
      value={formatAnswer(value, question)}
      disabled
      multiline
      minRows={2}
    />
  );
};

// ---------------------------------------------------------------------------
// STATUS FILTER OPTIONS
// ---------------------------------------------------------------------------

const STATUS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Statuses' },
  { value: 'Not started', label: 'Not started' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'Submitted', label: 'Submitted' },
  { value: 'Under Review', label: 'Under Review' },
  { value: 'Clarification Needed', label: 'Clarification Needed' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
];

const RISK_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'All Risk Levels' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'critical', label: 'Critical' },
  { value: 'not_defined', label: 'Not Defined' },
];

// ---------------------------------------------------------------------------
// Review Dialog
// ---------------------------------------------------------------------------

interface ReviewDialogProps {
  open: boolean;
  app: ApplicationRow | null;
  questions: RiskQuestion[];
  questionsLoading: boolean;
  /** From template.active_frameworks — owner-visible framework scope. */
  frameworkScopeHint: string | null;
  onClose: () => void;
  onSubmitReview: (
    assessmentId: string,
    decision: 'Approved' | 'Needs More Information',
    reviewNotes: string,
    applicationId: string
  ) => Promise<void>;
  submitting: boolean;
}

const ReviewDialog: React.FC<ReviewDialogProps> = ({
  open,
  app,
  questions,
  questionsLoading,
  frameworkScopeHint,
  onClose,
  onSubmitReview,
  submitting,
}) => {
  const { resolvedMode } = useTheme();
  const [reviewNotes, setReviewNotes] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReviewNotes(app?.assessment?.review_notes || '');
      setActiveTab(0);
      setActionError(null);
    }
  }, [open, app]);

  if (!app || !app.assessment) return null;

  const assessment = app.assessment;
  const rawAnswers = (assessment.answers as Record<string, unknown>) || {};

  // If none of the current question UUIDs appear as keys, this is a legacy
  // assessment saved with old UUIDs (caused by re-seeding without stable IDs).
  // Fall back to positional matching: pair sorted answer values with questions
  // sorted by question_number so the GRC admin can still read the responses.
  const hasUuidMatch = questions.some((q) => rawAnswers[q.id] !== undefined);
  let answers: Record<string, unknown>;
  if (hasUuidMatch || Object.keys(rawAnswers).length === 0) {
    answers = rawAnswers;
  } else {
    const answerValues = Object.values(rawAnswers);
    const sortedQuestions = [...questions].sort((a, b) => a.question_number - b.question_number);
    const remapped: Record<string, unknown> = {};
    sortedQuestions.forEach((q, idx) => {
      if (answerValues[idx] !== undefined) remapped[q.id] = answerValues[idx];
    });
    answers = remapped;
  }

  // Group questions by category (a question may appear in multiple tabs)
  const categories = Array.from(
    new Set(questions.flatMap((q) => questionCategories(q)))
  ).sort();
  const currentCategory = categories[activeTab] || '';
  const questionsInCategory = questions.filter((q) => questionCategories(q).includes(currentCategory));

  const displayStatus = computeDisplayStatus(app);

  async function handleDecision(decision: 'Approved' | 'Needs More Information') {
    setActionError(null);
    try {
      await onSubmitReview(assessment.id, decision, reviewNotes, app?.id || '');
    } catch (err: unknown) {
      setActionError(err instanceof Error ? err.message : 'An error occurred');
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: 1.5,
          px: 3,
        }}
      >
        <Box sx={{ pr: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700}>
            Review Risk Assessment — {app.name}
          </Typography>
          {frameworkScopeHint ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, maxWidth: { xs: '100%', sm: 720 }, lineHeight: 1.4 }}
            >
              {frameworkScopeHint}
            </Typography>
          ) : null}
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ mt: -0.25 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ display: 'flex', height: '70vh', overflow: 'hidden' }}>
          {/* Left panel — Q&A */}
          <Box
            sx={{
              width: '60%',
              borderRight: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            {questionsLoading ? (
              <Box sx={{ p: 3 }}>
                {[...Array(6)].map((_, i) => (
                  <Skeleton key={i} height={56} sx={{ mb: 1 }} />
                ))}
              </Box>
            ) : questions.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Alert severity="info">No questions found in template.</Alert>
              </Box>
            ) : (
              <>
                <Tabs
                  value={activeTab}
                  onChange={(_, v) => setActiveTab(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'grey.50',
                    minHeight: 44,
                    flexShrink: 0,
                  }}
                >
                  {categories.map((cat) => (
                    <Tab
                      key={cat}
                      label={cat}
                      sx={{ fontSize: '0.75rem', minHeight: 44, textTransform: 'none' }}
                    />
                  ))}
                </Tabs>

                <Box sx={{ overflowY: 'auto', flex: 1, px: 3, py: 2 }}>
                  {questionsInCategory.map((q, idx) => {
                    const answerValue = answers[q.id];
                    const answered = answerValue !== undefined && answerValue !== null && answerValue !== '';
                    return (
                      <Box key={q.id} sx={{ mb: 2.5 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 0.5, fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: 0.5 }}
                        >
                          Q{q.question_number ?? idx + 1}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                          {q.question_text}
                        </Typography>
                        <Box
                          sx={{
                            bgcolor: answered ? 'grey.50' : 'warning.50',
                            border: '1px solid',
                            borderColor: answered ? 'grey.200' : 'warning.200',
                            borderRadius: 1,
                            px: 1.5,
                            py: 1,
                          }}
                        >
                          <ReadOnlyRiskAnswer question={q} value={answerValue} />
                        </Box>
                        {idx < questionsInCategory.length - 1 && (
                          <Divider sx={{ mt: 2 }} />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </>
            )}
          </Box>

          {/* Right panel — App info + actions */}
          <Box
            sx={{
              width: '40%',
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
              p: 2,
              gap: 1.5,
            }}
          >
            {/* Compact info card */}
            <Card variant="outlined" sx={{ borderRadius: 2, flexShrink: 0 }}>
              <CardContent sx={{ p: '12px !important' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Application
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                      {app.name}
                    </Typography>
                    {app.business_function && app.business_function !== app.name && (
                      <Typography variant="caption" color="text.secondary">{app.business_function}</Typography>
                    )}
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <Chip size="small" {...riskChipProps(assessment.status === 'Approved' ? (assessment.risk_level || app.risk_level) : app.risk_level || null, resolvedMode)} />
                    {(() => {
                      const props = statusChipProps(displayStatus);
                      return <Chip size="small" label={props.label} color={props.color} variant={props.variant} sx={props.sx} />;
                    })()}
                    {assessment.calculated_risk_score != null && (
                      <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                        Risk Score: <strong>{assessment.calculated_risk_score}</strong>
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Divider sx={{ my: 1 }} />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Submitted By</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                        {assessment.submitted_by_name || '—'}
                      </Typography>
                      {assessment.delegated_by_name && (
                        <Chip label="delegated" size="small" sx={{ fontSize: '0.6rem', height: 16, ...chipSxFromSurface(getStatusSurface('warning', resolvedMode)) }} />
                      )}
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary">Submitted On</Typography>
                    <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                      {formatDateTime(assessment.submitted_at)}
                    </Typography>
                  </Box>
                  {assessment.assigned_to_name && (
                    <Box sx={{ gridColumn: '1 / -1' }}>
                      <Typography variant="caption" color="text.secondary">Assigned To</Typography>
                      <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.8rem' }}>
                        {assessment.assigned_to_name}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>

            <Card variant="outlined" sx={{ borderRadius: 2, flexShrink: 0 }}>
              <CardContent sx={{ p: '12px !important' }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Score breakdown
                </Typography>
                {assessment.calculated_risk_score == null ? (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    This application has no score yet. Approve and Clarify stay in this workspace.
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" fontWeight={600} sx={{ mt: 0.5 }}>
                      {`Score ${assessment.calculated_risk_score} is ${scoreBand(assessment.calculated_risk_score)?.level} (${scoreBand(assessment.calculated_risk_score)?.range}).`}
                    </Typography>
                    {recommendedControl(assessment) ? (
                      <>
                        <Typography variant="body2" sx={{ mt: 0.75 }}>
                          {`Control: ${recommendedControl(assessment)?.name}`}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {recommendedControl(assessment)?.sentence}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        The control sentence appears after the owner saves answers on the current score bands.
                      </Typography>
                    )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* GRC Notes */}
            <Box sx={{ flexShrink: 0 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mb: 0.75 }}>
                GRC Review Notes
              </Typography>
              <TextField
                multiline
                minRows={3}
                maxRows={6}
                fullWidth
                variant="outlined"
                placeholder="Add your review notes here..."
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value)}
                size="small"
              />
            </Box>

            {actionError && (
              <Alert severity="error" onClose={() => setActionError(null)} sx={{ py: 0.5 }}>
                {actionError}
              </Alert>
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1.5, mt: 'auto' }}>
              <Button
                variant="contained"
                color="success"
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <CheckCircleIcon />
                  )
                }
                disabled={submitting}
                onClick={() => handleDecision('Approved')}
                fullWidth
                sx={{ fontWeight: 600 }}
              >
                Approve
              </Button>
              <Button
                variant="outlined"
                color="warning"
                startIcon={
                  submitting ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <HelpOutlineIcon />
                  )
                }
                disabled={submitting}
                onClick={() => handleDecision('Needs More Information')}
                fullWidth
                sx={{ fontWeight: 600 }}
              >
                Clarify
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const RiskAssessmentReview: React.FC = () => {
  const { user } = useAuth();
  const { resolvedMode } = useTheme();

  // Data
  const [applications, setApplications] = useState<ApplicationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Template questions (scoped list; framework hint from template)
  const [questions, setQuestions] = useState<RiskQuestion[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [frameworkScopeHint, setFrameworkScopeHint] = useState<string | null>(null);

  // Filters
  const [searchText, setSearchText] = useState('');
  const [whatIfApp, setWhatIfApp] = useState('Selected application');
  const [whatIfScore, setWhatIfScore] = useState('80');
  const [whatIfResult, setWhatIfResult] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [riskFilter, setRiskFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [listingSort, setListingSort] = useState<ListingSortValue>(DEFAULT_LISTING_SORT);

  // GRC admin users (for assignee picker)
  const [grcAdmins, setGrcAdmins] = useState<GrcAdminUser[]>([]);

  // Review dialog
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedApp, setSelectedApp] = useState<ApplicationRow | null>(null);
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewSuccess, setReviewSuccess] = useState<string | null>(null);

  // Assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<ApplicationRow | null>(null);
  const [assignUserId, setAssignUserId] = useState('');
  const [submittingAssign, setSubmittingAssign] = useState(false);

  // Start review dialog
  const [startReviewDialogOpen, setStartReviewDialogOpen] = useState(false);
  const [startReviewTarget, setStartReviewTarget] = useState<ApplicationRow | null>(null);
  const [startReviewUserId, setStartReviewUserId] = useState('');
  const [submittingStartReview, setSubmittingStartReview] = useState(false);

  // ---------------------------------------------------------------------------
  // Fetch helpers
  // ---------------------------------------------------------------------------

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      if (!hasAuthSession()) {
        throw new Error('Please log in to view risk assessments.');
      }
      const res = await fetch(`${API_ROOT}/risk-assessment/admin/all-assessments`, authFetchInit());
      if (!res.ok) throw new Error(`Failed to load assessments (${res.status})`);
      const data: AllAssessmentsResponse = await res.json();
      if (!data.success) throw new Error('Server returned an error');
      setApplications(data.applications);
    } catch (err: unknown) {
      setFetchError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const tplRes = await fetch(`${API_ROOT}/risk-assessment/template`, authFetchInit());
      if (!tplRes.ok) throw new Error(`Failed to load questionnaire template (${tplRes.status})`);
      const tplData = await tplRes.json();
      const templateId = tplData?.template?.id as string | undefined;
      if (tplData?.template) {
        setFrameworkScopeHint(formatFrameworkScopeHint(tplData.template.active_frameworks));
      } else {
        setFrameworkScopeHint(null);
      }
      if (!templateId) {
        setQuestions([]);
        return;
      }
      // Same scoped set application owners see (active_frameworks + visibility), not full GRC catalog.
      let raw: unknown[] = Array.isArray(tplData?.template?.questions) ? tplData.template.questions : [];
      if (raw.length === 0) {
        const scopedRes = await fetch(`${API_ROOT}/risk-assessment/template/${templateId}/questions`, authFetchInit());
        if (!scopedRes.ok) throw new Error(`Failed to load scoped questions (${scopedRes.status})`);
        const scopedData: { success?: boolean; questions?: unknown[] } = await scopedRes.json();
        raw = scopedData.success && Array.isArray(scopedData.questions) ? scopedData.questions : [];
      }
      setQuestions(normalizeQuestionsFromApi(raw));
    } catch (err) {
      console.error('Failed to load template questions:', err);
      setQuestions([]);
      setFrameworkScopeHint(null);
    } finally {
      setQuestionsLoading(false);
    }
  }, []);

  const fetchGrcAdmins = useCallback(async () => {
    try {
      const res = await fetch(`${API_ROOT}/risk-assessment/admin/grc-admins`, authFetchInit());
      if (!res.ok) return;
      const data: { success: boolean; users: GrcAdminUser[] } = await res.json();
      if (data.success) setGrcAdmins(data.users);
    } catch (err) {
      console.error('Failed to load GRC admins:', err);
    }
  }, []);

  useEffect(() => {
    fetchApplications();
    fetchQuestions();
    fetchGrcAdmins();
  }, [fetchApplications, fetchQuestions, fetchGrcAdmins]);

  // ---------------------------------------------------------------------------
  // Review submission
  // ---------------------------------------------------------------------------

  const handleSubmitReview = useCallback(
    async (
      assessmentId: string,
      decision: 'Approved' | 'Needs More Information',
      reviewNotes: string,
      applicationId: string
    ) => {
      setSubmittingReview(true);
      try {
        const res = await fetch(
          `${API_ROOT}/risk-assessment/assessments/${assessmentId}/review`,
          authFetchInit({
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              decision,
              review_notes: reviewNotes,
              reviewer_id: user?.id,
              application_id: applicationId || undefined,
            }),
          })
        );
        if (!res.ok) {
          const errBody = await res.json().catch(() => ({}));
          throw new Error(errBody?.message || `Request failed (${res.status})`);
        }
        setReviewDialogOpen(false);
        setSelectedApp(null);
        setReviewSuccess(
          decision === 'Approved'
            ? 'Assessment approved successfully.'
            : 'Clarification requested successfully.'
        );
        // Refresh table
        await fetchApplications();
      } finally {
        setSubmittingReview(false);
      }
    },
    [user, fetchApplications]
  );

  // ---------------------------------------------------------------------------
  // Open review dialog
  // ---------------------------------------------------------------------------

  function openReview(app: ApplicationRow) {
    setSelectedApp(app);
    setReviewDialogOpen(true);
  }

  // ---------------------------------------------------------------------------
  // Assign reviewer
  // ---------------------------------------------------------------------------

  function openAssignDialog(app: ApplicationRow) {
    setAssignTarget(app);
    setAssignUserId(app.assessment?.assigned_to || '');
    setAssignDialogOpen(true);
  }

  // Only updates the assignee — does NOT change assessment status
  const handleAssign = useCallback(async () => {
    if (!assignTarget || !assignTarget.assessment) return;
    setSubmittingAssign(true);
    try {
      const res = await fetch(
        `${API_ROOT}/risk-assessment/assessments/${assignTarget.assessment.id}/assign`,
        authFetchInit({
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assigned_to: assignUserId || null }),
        })
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Request failed (${res.status})`);
      }
      setAssignDialogOpen(false);
      setAssignTarget(null);
      await fetchApplications();
    } catch (err) {
      console.error('Failed to update assignee:', err);
    } finally {
      setSubmittingAssign(false);
    }
  }, [assignTarget, assignUserId, fetchApplications]);

  function openStartReviewDialog(app: ApplicationRow) {
    setStartReviewTarget(app);
    setStartReviewUserId(app.assessment?.assigned_to || '');
    setStartReviewDialogOpen(true);
  }

  // Creates the assessment (if needed) and sets status to 'In Review'
  const handleStartReview = useCallback(async () => {
    if (!startReviewTarget) return;
    setSubmittingStartReview(true);
    try {
      const res = await fetch(
        `${API_ROOT}/risk-assessment/admin/assign-review`,
        authFetchInit({
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            application_id: startReviewTarget.id,
            assigned_to: startReviewUserId || null,
          }),
        })
      );
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.message || `Request failed (${res.status})`);
      }
      setStartReviewDialogOpen(false);
      setStartReviewTarget(null);
      await fetchApplications();
    } catch (err) {
      console.error('Failed to start review:', err);
    } finally {
      setSubmittingStartReview(false);
    }
  }, [startReviewTarget, startReviewUserId, fetchApplications]);

  // ---------------------------------------------------------------------------
  // Filtered rows
  // ---------------------------------------------------------------------------

  const filteredApps = applications.filter((app) => {
    const displayStatus = computeDisplayStatus(app);

    if (searchText.trim()) {
      if (!app.name.toLowerCase().includes(searchText.trim().toLowerCase())) return false;
    }

    if (statusFilter !== 'all') {
      if (displayStatus !== statusFilter) return false;
    }

    if (riskFilter !== 'all') {
      const rl = (app.assessment?.risk_level || app.risk_level || '').toLowerCase();
      if (riskFilter === 'not_defined') {
        if (rl && rl !== '') return false;
      } else {
        if (rl !== riskFilter) return false;
      }
    }

    return true;
  });

  useEffect(() => {
    setPage(1);
  }, [searchText, statusFilter, riskFilter, rowsPerPage, listingSort]);

  const totalPages = Math.max(1, Math.ceil(filteredApps.length / rowsPerPage));
  const safePage = Math.min(page, totalPages);
  const startIndex = (safePage - 1) * rowsPerPage;
  const paginatedApps = sortListing(filteredApps, listingSort, {
    getName: (app) => app.name,
    getDate: (app) => app.assessment?.submitted_at,
  }).slice(startIndex, startIndex + rowsPerPage);

  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = { all: applications.length };
    for (const app of applications) {
      const status = computeDisplayStatus(app);
      counts[status] = (counts[status] || 0) + 1;
    }
    return counts;
  }, [applications]);

  const pendingReviewCount =
    (statusCounts['Submitted'] || 0) + (statusCounts['Under Review'] || 0);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
        <RateReviewIcon sx={{ color: 'primary.main', fontSize: 22 }} />
        <Box>
          <Typography sx={{ fontSize: '1.1rem', fontWeight: 700, lineHeight: 1.2 }}>Risk Assessment Review</Typography>
          <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary' }}>
            Review and action risk assessments submitted across all registered applications.
          </Typography>
        </Box>
        {!loading && (
          <Chip
            label={`${pendingReviewCount} pending review`}
            size="small"
            color={pendingReviewCount > 0 ? 'warning' : 'default'}
            sx={{ ml: 'auto', fontWeight: 600, fontSize: UI_TOKENS.text.helper }}
          />
        )}
      </Box>

      <Box sx={{ mb: 2, p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
        <Typography sx={{ fontSize: '0.9rem', fontWeight: 700, mb: 0.5 }}>What-if</Typography>
        <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', mb: 1 }}>
          Preview the control for a score. This does not save the policy or submit an assessment.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" label="Application" value={whatIfApp} onChange={(e) => setWhatIfApp(e.target.value)} />
          <TextField size="small" label="Score" type="number" value={whatIfScore} onChange={(e) => setWhatIfScore(e.target.value)} sx={{ width: 100 }} />
          <Button
            size="small"
            variant="outlined"
            onClick={async () => {
              const res = await fetch(`${API_ROOT}/risk-assessment/what-if`, authFetchInit({
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ score: Number(whatIfScore), application_name: whatIfApp }),
              }));
              const body = await res.json();
              setWhatIfResult(body?.wrote === false ? (body?.data?.owner_sentence || 'No control sentence.') : (body?.error || 'Preview failed.'));
            }}
          >
            Preview control
          </Button>
        </Box>
        {whatIfResult && <Typography sx={{ mt: 1, fontSize: '0.82rem' }}>{whatIfResult}</Typography>}
      </Box>

      {/* Status summary */}
      {!loading && applications.length > 0 && (
        <Stack gap={0.75} sx={{ mb: 2 }}>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            <Chip
              label={`All (${statusCounts.all})`}
              size="small"
              clickable
              variant={statusFilter === 'all' ? 'filled' : 'outlined'}
              color={statusFilter === 'all' ? 'primary' : 'default'}
              onClick={() => setStatusFilter('all')}
              sx={{ fontSize: UI_TOKENS.text.helper, fontWeight: 600 }}
            />
            {STATUS_ORDER.map((key) => (
              <Chip
                key={key}
                label={`${key} (${statusCounts[key] || 0})`}
                size="small"
                clickable
                variant={statusFilter === key ? 'filled' : 'outlined'}
                color={statusFilter === key ? 'primary' : 'default'}
                onClick={() => setStatusFilter(key)}
                sx={{ fontSize: UI_TOKENS.text.helper, fontWeight: 600 }}
              />
            ))}
          </Stack>
          <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary' }}>
            {`${STATUS_ORDER.reduce((sum, key) => sum + (statusCounts[key] || 0), 0)} of ${statusCounts.all} applications sit in exactly one status.`}
          </Typography>
        </Stack>
      )}

      {/* Success banner */}
      {reviewSuccess && (
        <Alert
          severity="success"
          onClose={() => setReviewSuccess(null)}
          sx={{ mb: 2 }}
        >
          {reviewSuccess}
        </Alert>
      )}

      {/* Fetch error */}
      {fetchError && (
        <Alert severity="error" sx={{ mb: 2 }} action={
          <Button color="inherit" size="small" onClick={fetchApplications}>
            Retry
          </Button>
        }>
          {fetchError}
        </Alert>
      )}

      {/* Filters row */}
      <Paper variant="outlined" sx={{ mb: 2, borderRadius: 2, p: '10px 14px' }}>
          <Box
            sx={{
              display: 'flex',
              gap: 1.5,
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <FilterListIcon sx={{ color: 'text.secondary', fontSize: 18 }} />
            <TextField
              size="small"
              placeholder="Search by application name…"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              InputProps={{ startAdornment: <SearchIcon sx={{ mr: 0.5, color: 'text.secondary', fontSize: 18 }} /> }}
              sx={{ minWidth: 240, ...INPUT_SX }}
            />
            <FormControl size="small" sx={{ minWidth: 180, ...INPUT_SX }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
                MenuProps={MENU_SX}
              >
                {STATUS_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160, ...INPUT_SX }}>
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={riskFilter}
                label="Risk Level"
                onChange={(e) => setRiskFilter(e.target.value)}
                MenuProps={MENU_SX}
              >
                {RISK_FILTER_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <ListingSortSelect
              value={listingSort}
              onChange={setListingSort}
              sx={INPUT_SX}
              menuProps={MENU_SX}
            />
            <Typography sx={{ fontSize: UI_TOKENS.text.buttonSm, color: 'text.secondary', ml: 'auto' }}>
              {filteredApps.length} of {applications.length} application{applications.length !== 1 ? 's' : ''}
            </Typography>
          </Box>
      </Paper>

      {/* Main table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 'calc(100vh - 380px)', overflowX: 'auto' }}>
          <Table stickyHeader size="small" sx={{ ...listingTableSx, tableLayout: 'fixed', minWidth: 1080, width: '100%' }}>
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map(({ label, width }) => (
                  <TableCell
                    key={label}
                    sx={{
                      width,
                      fontWeight: 700,
                      fontSize: UI_TOKENS.text.helper,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      color: 'text.secondary',
                      bgcolor: 'grey.50',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                      whiteSpace: 'nowrap',
                      py: 1,
                    }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {loading ? (
                [...Array(8)].map((_, i) => (
                  <TableRow key={i}>
                    {TABLE_COLUMNS.map((col) => (
                      <TableCell key={col.label}>
                        <Skeleton height={28} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredApps.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={TABLE_COLUMNS.length} align="center" sx={{ py: 6 }}>
                    <Typography variant="body2" color="text.secondary">
                      {applications.length === 0
                        ? 'No applications found for this tenant.'
                        : 'No applications match the current filters.'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedApps.map((app, idx) => {
                  const displayStatus = computeDisplayStatus(app);
                  const statusProps = statusChipProps(displayStatus);
                  // Show GRC-approved risk level, or fall back to admin-set app risk level
                  const effectiveRisk = app.assessment?.status === 'Approved'
                    ? (app.assessment?.risk_level || app.risk_level)
                    : app.risk_level;
                  const riskProps = riskChipProps(effectiveRisk || null, resolvedMode);
                  const isReviewable =
                    app.assessment?.status === 'In Review';
                  const isDelegated = Boolean(app.assessment?.delegated_by_name);

                  return (
                    <TableRow
                      key={app.id}
                      hover
                      sx={{
                        bgcolor: idx % 2 === 0 ? 'background.paper' : 'grey.50',
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {/* Application */}
                      <TableCell>
                        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 600 }} noWrap>
                          {app.name}
                        </Typography>
                        {app.business_function && (
                          <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }} noWrap>
                            {app.business_function}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Owners */}
                      <TableCell>
                        <Typography sx={{ fontSize: UI_TOKENS.text.bodySm }} noWrap title={formatOwners(app.primary_owners)}>
                          {formatOwners(app.primary_owners)}
                        </Typography>
                      </TableCell>

                      {/* Submission (by + date) */}
                      <TableCell>
                        {app.assessment?.submitted_by_name ? (
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                              <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, fontWeight: 500 }} noWrap>
                                {app.assessment.submitted_by_name}
                              </Typography>
                              {isDelegated && (
                                <Chip
                                  label="delegated"
                                  size="small"
                                  sx={{
                                    fontSize: UI_TOKENS.text.helper,
                                    height: 18,
                                    ...chipSxFromSurface(getStatusSurface('warning', resolvedMode)),
                                    '& .MuiChip-label': { px: 0.75 },
                                  }}
                                />
                              )}
                            </Box>
                            <Typography sx={{ fontSize: UI_TOKENS.text.helper, color: 'text.secondary', mt: 0.25 }}>
                              {formatDateTime(app.assessment.submitted_at)}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography sx={{ fontSize: UI_TOKENS.text.bodySm, color: 'text.disabled' }}>Not submitted</Typography>
                        )}
                      </TableCell>

                      {/* Assignee */}
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: UI_TOKENS.text.bodySm,
                              fontStyle: app.assessment?.assigned_to_name ? 'normal' : 'italic',
                              color: app.assessment?.assigned_to_name ? 'text.primary' : 'text.secondary',
                            }}
                            noWrap
                          >
                            {app.assessment?.assigned_to_name || 'Unassigned'}
                          </Typography>
                          {app.assessment && (
                            <Tooltip title="Change assignee">
                              <IconButton size="small" sx={{ p: 0.25, flexShrink: 0 }} onClick={() => openAssignDialog(app)}>
                                <EditIcon sx={{ fontSize: 14 }} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      {/* Score */}
                      <TableCell>
                        <Typography
                          sx={{
                            fontSize: UI_TOKENS.text.bodySm,
                            fontWeight: 600,
                            fontFamily: 'monospace',
                            color: app.assessment?.calculated_risk_score != null ? 'text.primary' : 'text.disabled',
                          }}
                        >
                          {app.assessment?.calculated_risk_score ?? '—'}
                        </Typography>
                      </TableCell>

                      {/* Risk */}
                      <TableCell>
                        <Chip
                          size="small"
                          {...riskProps}
                          sx={{
                            ...((riskProps as { sx?: object }).sx || {}),
                            height: 22,
                            fontSize: UI_TOKENS.text.helper,
                            maxWidth: '100%',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* Status */}
                      <TableCell>
                        <Chip
                          size="small"
                          label={statusProps.label}
                          color={statusProps.color}
                          variant={statusProps.variant}
                          sx={{
                            ...statusProps.sx,
                            height: 22,
                            fontSize: UI_TOKENS.text.helper,
                            fontWeight: 600,
                            maxWidth: '100%',
                            '& .MuiChip-label': { px: 1 },
                          }}
                        />
                      </TableCell>

                      {/* Actions */}
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'nowrap' }}>
                          <Tooltip title={app.assessment?.status === 'Approved' ? 'Re-assign for re-review' : 'Assign reviewer and start review'}>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<PersonAddIcon sx={{ fontSize: '0.95rem !important' }} />}
                              onClick={() => openStartReviewDialog(app)}
                              sx={{ fontSize: UI_TOKENS.text.labelXs, py: 0.35, px: 1, whiteSpace: 'nowrap', minWidth: 0 }}
                            >
                              {app.assessment?.status === 'Approved' ? 'Re-assign' : 'Assign'}
                            </Button>
                          </Tooltip>
                          <Tooltip title={isReviewable ? 'Open review workspace' : 'Available when status is Under Review'}>
                            <span>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<RateReviewIcon sx={{ fontSize: '0.95rem !important' }} />}
                                disabled={!isReviewable}
                                onClick={() => openReview(app)}
                                sx={{ fontSize: UI_TOKENS.text.labelXs, py: 0.35, px: 1.25, whiteSpace: 'nowrap', minWidth: 0 }}
                              >
                                Review
                              </Button>
                            </span>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <ListingPagination
          page={safePage}
          totalPages={totalPages}
          totalItems={filteredApps.length}
          pageSize={rowsPerPage}
          onPageChange={setPage}
          onPageSizeChange={setRowsPerPage}
          pageSizeSelectSx={INPUT_SX}
          pageSizeMenuProps={MENU_SX}
        />
      </Paper>

      {/* Review Dialog */}
      <ReviewDialog
        open={reviewDialogOpen}
        app={selectedApp}
        questions={questions}
        questionsLoading={questionsLoading}
        frameworkScopeHint={frameworkScopeHint}
        onClose={() => {
          setReviewDialogOpen(false);
          setSelectedApp(null);
        }}
        onSubmitReview={handleSubmitReview}
        submitting={submittingReview}
      />

      {/* Change Assignee Dialog (no status change) */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700}>Change Assignee</Typography>
          <IconButton size="small" onClick={() => setAssignDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Update the assigned reviewer for <strong>{assignTarget?.name}</strong>. This will <strong>not</strong> change the assessment status.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>GRC Admin Reviewer</InputLabel>
            <Select
              value={assignUserId}
              onChange={(e) => setAssignUserId(e.target.value)}
              label="GRC Admin Reviewer"
            >
              <MenuItem value=""><em>Unassigned</em></MenuItem>
              {grcAdmins.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setAssignDialogOpen(false)} disabled={submittingAssign}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={submittingAssign}
            startIcon={submittingAssign ? <CircularProgress size={14} color="inherit" /> : undefined}>
            Save Assignee
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign for Review Dialog (changes status to In Review) */}
      <Dialog open={startReviewDialogOpen} onClose={() => setStartReviewDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography fontWeight={700}>
            {startReviewTarget?.assessment?.status === 'Approved' ? 'Re-Assign for Re-Review' : 'Assign for Review'}
          </Typography>
          <IconButton size="small" onClick={() => setStartReviewDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Assign a reviewer for <strong>{startReviewTarget?.name}</strong> and set status to <strong>In Review</strong>.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>GRC Admin Reviewer</InputLabel>
            <Select
              value={startReviewUserId}
              onChange={(e) => setStartReviewUserId(e.target.value)}
              label="GRC Admin Reviewer"
            >
              <MenuItem value=""><em>Unassigned</em></MenuItem>
              {grcAdmins.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.first_name} {u.last_name}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setStartReviewDialogOpen(false)} disabled={submittingStartReview}>Cancel</Button>
          <Button variant="contained" onClick={handleStartReview} disabled={submittingStartReview}
            startIcon={submittingStartReview ? <CircularProgress size={14} color="inherit" /> : undefined}>
            {startReviewTarget?.assessment?.status === 'Approved' ? 'Re-Review' : 'Start Review'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RiskAssessmentReview;
